import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import { motion } from 'framer-motion';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';

const steps = ['Coin Info', 'Your Details', 'Additional'];

const ApplicationForm = ({ open, onClose, coin }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    // Coin details
    coinName: coin?.name || '',
    coinYear: coin?.year || '',
    coinGrade: coin?.grade || '',
    coinPrice: coin?.price || '',
    
    // Personal details
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    postalCode: '',
    
    // Supporting information
    collectibleExperience: '',
    purposeOfPurchase: '',
    additionalInfo: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    // Prepare email content
    const subject = `Price Inquiry: ${formData.coinName}`;
    const body = `
      COIN INQUIRY APPLICATION
      
      COIN DETAILS:
      - Coin: ${formData.coinName}
      - Year: ${formData.coinYear}
      - Grade: ${formData.coinGrade}
      - Listed Price: ${formData.coinPrice}
      
      APPLICANT INFORMATION:
      - Full Name: ${formData.fullName}
      - Email: ${formData.email}
      - Phone: ${formData.phone}
      - Address: ${formData.address}
      - City: ${formData.city}
      - Country: ${formData.country}
      - Postal Code: ${formData.postalCode}
      
      ADDITIONAL INFORMATION:
      - Collecting Experience: ${formData.collectibleExperience}
      - Purpose of Purchase: ${formData.purposeOfPurchase}
      - Additional Notes: ${formData.additionalInfo}
    `;

    // Create mailto link
    const mailtoLink = `mailto:john.asfar@worldwidecorporation.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Open default email client
    window.location.href = mailtoLink;
    
    setLoading(false);
    setSuccess(true);
    
    // Close after success
    setTimeout(() => {
      setSuccess(false);
      setActiveStep(0);
      onClose();
    }, 2000);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ py: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#2D2D2D', fontWeight: 600, mb: 1 }}>
              Coin Details
            </Typography>
            <TextField
              fullWidth
              label="Coin Name"
              name="coinName"
              value={formData.coinName}
              onChange={handleChange}
              size="small"
              margin="dense"
              required
              disabled
            />
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Year"
                  name="coinYear"
                  value={formData.coinYear}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  disabled
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Grade"
                  name="coinGrade"
                  value={formData.coinGrade}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  disabled
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              label="Listed Price"
              name="coinPrice"
              value={formData.coinPrice}
              onChange={handleChange}
              size="small"
              margin="dense"
              disabled
            />
          </Box>
        );
      case 1:
        return (
          <Box sx={{ py: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#2D2D2D', fontWeight: 600, mb: 1 }}>
              Your Details
            </Typography>
            <TextField
              fullWidth
              label="Full Name"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              size="small"
              margin="dense"
              required
              InputProps={{
                startAdornment: <PersonIcon sx={{ color: 'primary.main', mr: 0.5, fontSize: 16 }} />
              }}
            />
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              size="small"
              margin="dense"
              required
              InputProps={{
                startAdornment: <EmailIcon sx={{ color: 'primary.main', mr: 0.5, fontSize: 16 }} />
              }}
            />
            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              size="small"
              margin="dense"
              required
              InputProps={{
                startAdornment: <PhoneIcon sx={{ color: 'primary.main', mr: 0.5, fontSize: 16 }} />
              }}
            />
            <TextField
              fullWidth
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              size="small"
              margin="dense"
              required
              InputProps={{
                startAdornment: <LocationOnIcon sx={{ color: 'primary.main', mr: 0.5, fontSize: 16 }} />
              }}
            />
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  required
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              label="Country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              size="small"
              margin="dense"
              required
            />
          </Box>
        );
      case 2:
        return (
          <Box sx={{ py: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#2D2D2D', fontWeight: 600, mb: 1 }}>
              Additional Information
            </Typography>
            <FormControl fullWidth size="small" margin="dense">
              <InputLabel>Collecting Experience</InputLabel>
              <Select
                name="collectibleExperience"
                value={formData.collectibleExperience}
                onChange={handleChange}
                label="Collecting Experience"
              >
                <MenuItem value="beginner">Beginner (0-2 years)</MenuItem>
                <MenuItem value="intermediate">Intermediate (3-5 years)</MenuItem>
                <MenuItem value="advanced">Advanced (5-10 years)</MenuItem>
                <MenuItem value="expert">Expert (10+ years)</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth size="small" margin="dense">
              <InputLabel>Purpose of Purchase</InputLabel>
              <Select
                name="purposeOfPurchase"
                value={formData.purposeOfPurchase}
                onChange={handleChange}
                label="Purpose of Purchase"
              >
                <MenuItem value="collection">Personal Collection</MenuItem>
                <MenuItem value="investment">Investment</MenuItem>
                <MenuItem value="gift">Gift</MenuItem>
                <MenuItem value="resale">Resale</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Additional Information"
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleChange}
              size="small"
              margin="dense"
              multiline
              rows={2}
              placeholder="Any additional details..."
              InputProps={{
                startAdornment: <DescriptionIcon sx={{ color: 'primary.main', mr: 0.5, fontSize: 16, alignSelf: 'flex-start', mt: 0.5 }} />
              }}
            />
            
            <Alert severity="info" sx={{ mt: 1, py: 0.5, fontSize: '0.75rem' }}>
              Our team will respond within 24-48 hours.
            </Alert>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm" // Changed from md to sm
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1, // Reduced padding
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Typography variant="h5" color="primary" gutterBottom sx={{ fontSize: '1.3rem' }}>
          Price Inquiry
        </Typography>
        <Typography variant="caption" color="#616161" sx={{ fontSize: '0.7rem' }}>
          {coin?.name || 'Complete this form'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Stepper activeStep={activeStep} sx={{ my: 1, py: 1 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.75rem' } }}>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {success ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Typography variant="h6" color="primary" gutterBottom sx={{ fontSize: '1.1rem' }}>
                Application Submitted!
              </Typography>
              <Typography variant="caption" color="#616161" sx={{ fontSize: '0.7rem' }}>
                We'll contact you within 24-48 hours.
              </Typography>
            </motion.div>
          </Box>
        ) : (
          getStepContent(activeStep)
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', p: 2, pt: 0 }}>
        <Button
          onClick={onClose}
          size="small"
          variant="outlined"
          disabled={loading}
          sx={{ fontSize: '0.75rem', py: 0.5 }}
        >
          Cancel
        </Button>
        <Box>
          {activeStep > 0 && (
            <Button
              onClick={handleBack}
              size="small"
              sx={{ mr: 1, fontSize: '0.75rem' }}
              disabled={loading}
            >
              Back
            </Button>
          )}
          {activeStep === steps.length - 1 ? (
            <Button
              onClick={handleSubmit}
              size="small"
              variant="contained"
              disabled={loading}
              startIcon={loading && <CircularProgress size={14} />}
              sx={{ fontSize: '0.75rem', py: 0.5 }}
            >
              {loading ? 'Submitting...' : 'Submit'}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              size="small"
              variant="contained"
              sx={{ fontSize: '0.75rem', py: 0.5 }}
            >
              Next
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ApplicationForm;